import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { Alert, Loading, Spacer, Text, useForm } from "@venuepos/react-common";
import {
    GQIntegrationQuery,
    useAccountChartByProductGroupLazyQuery,
    useMerchantChartOfAccountsLazyQuery,
    useSaveAccountChartByProductGroupMutation,
    useSaveMerchantChartOfAccountsMutation,
} from "graphql-sdk";
import produce from "immer";
import { IAccountChartByProductGroup, IMerchantChartOfAccountInput } from "lib";
import { useTranslation } from "locales";
import React, { useCallback, useEffect } from "react";
import { View } from "react-native";
import { Divider } from "react-native-paper";
import { useHandleMutationError } from "../../../hooks";
import { ChartOfAccounts } from "../chart-of-accounts";
import { AccountChartByProductGroup } from "./account-chart-by-product-group";

export function ChartOfAccountsTab({
    integration,
}: {
    integration: GQIntegrationQuery["integration"];
}) {
    const [t] = useTranslation();
    const { navigate } = useNavigation();
    const { handleMutationError } = useHandleMutationError();
    const [
        getProductGroupChartOfAccounts,
        {
            data: productGroupsCOAData,
            loading: productGroupsCOALoading,
            error: productGroupsCOAError,
        },
    ] = useAccountChartByProductGroupLazyQuery({
        variables: { integrationId: integration.id },
    });

    const [
        getGeneralChartOfAccounts,
        {
            data: generalCOAData,
            loading: generalCOALoading,
            error: generalCOAError,
        },
    ] = useMerchantChartOfAccountsLazyQuery({
        variables: { integrationId: integration.id },
    });

    const productGroupForm = useForm<{
        pgMap: IAccountChartByProductGroup[];
        generalAccountMap: IMerchantChartOfAccountInput[];
    }>({}, null);
    const [{ values }, { setValue, setDefaultValues }] = productGroupForm;

    const [saveProductGroupMap] = useSaveAccountChartByProductGroupMutation();
    const [saveMerchantChartOfAccounts] =
        useSaveMerchantChartOfAccountsMutation();

    const save = useCallback(async () => {
        if (!values) {
            return;
        }

        await handleMutationError(async () => {
            await saveMerchantChartOfAccounts({
                variables: {
                    integrationId: integration.id,
                    chartOfAccounts: values.generalAccountMap,
                },
            });

            await saveProductGroupMap({
                variables: {
                    integrationId: integration.id,
                    productGroupMap: values.pgMap.map(item => ({
                        accountNo: item.accountNo,
                        productGroupId: item.productGroup.id,
                    })),
                },
            });
        }, t("common.saved", "Saved"));
    }, [
        handleMutationError,
        integration.id,
        saveMerchantChartOfAccounts,
        saveProductGroupMap,
        t,
        values,
    ]);

    useFocusEffect(
        useCallback(() => {
            getProductGroupChartOfAccounts();
            getGeneralChartOfAccounts();
        }, [getProductGroupChartOfAccounts, getGeneralChartOfAccounts])
    );

    useEffect(() => {
        if (!integration || !integration.id) {
            navigate("INTEGRATIONS");
        }
    }, [integration, navigate]);

    useEffect(() => {
        if (!generalCOAData) {
            return;
        }

        if (!productGroupsCOAData) {
            return;
        }

        setDefaultValues({
            pgMap: productGroupsCOAData.accountChartByProductGroup.data.map(
                item => ({
                    accountNo: item.accountNo,
                    productGroupId: item.productGroupId,
                    productGroup: {
                        id: item.productGroup.id,
                        name: item.productGroup.name,
                        deletedAt: item.productGroup.deletedAt,
                    },
                })
            ),
            generalAccountMap: generalCOAData.merchantChartOfAccounts.data.map(
                item => ({
                    name: item.name,
                    chartOfAccountsId: item.chartOfAccountsId,
                    merchantAccountNo: item.merchantAccountNo,
                })
            ),
        });
    }, [generalCOAData, productGroupsCOAData, setDefaultValues]);

    return (
        <View>
            {/* General Merchant Chart of Accounts mapping */}
            {generalCOALoading ? (
                <Loading />
            ) : generalCOAError ? (
                <Alert type="error">
                    <View>
                        <Text>
                            {t(
                                "integration.general_account_chart_error",
                                "An error occured fetching the General account mapping."
                            )}
                        </Text>
                        <Text>{generalCOAError.name}</Text>
                        <Text>
                            {JSON.stringify(generalCOAError.graphQLErrors)}
                        </Text>
                    </View>
                </Alert>
            ) : generalCOAData ? (
                <ChartOfAccounts
                    chartOfAccounts={values?.generalAccountMap}
                    setValue={setValue}
                />
            ) : null}

            <Spacer />
            <Divider />

            <Spacer space={4} />

            {/* Product group to Account Number mapping */}
            {productGroupsCOALoading ? (
                <Loading />
            ) : productGroupsCOAError ? (
                <Alert type="error">
                    <View>
                        <Text>
                            {t(
                                "integration.product_group_account_chart_error",
                                "An error occurred fetching the Product Group account mapping."
                            )}
                        </Text>
                        <Text>{productGroupsCOAError.name}:</Text>
                        <Text>
                            {JSON.stringify(
                                productGroupsCOAError.graphQLErrors
                            )}
                        </Text>
                    </View>
                </Alert>
            ) : productGroupsCOAData ? (
                <AccountChartByProductGroup
                    productGroupMap={values?.pgMap}
                    onSave={save}
                    onUpdateMap={(productGroupId, accountNo) => {
                        if (!values?.pgMap) {
                            return;
                        }

                        setValue(
                            "pgMap",
                            produce(values.pgMap, draft => {
                                draft.map(item => {
                                    if (
                                        item.productGroup.id === productGroupId
                                    ) {
                                        item.accountNo = accountNo;
                                    }

                                    return item;
                                });
                            })
                        );
                    }}
                />
            ) : null}
        </View>
    );
}
